import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  useProdutoMedicamento, SalvarProdutoMedicamentoRequest,
} from "@/features/produtos/hooks/useProdutoMedicamento";

/** Estado inicial — produto não-controlado, uso humano, caixas. */
const EMPTY: SalvarProdutoMedicamentoRequest = {
  registroMs: "", codigoDcb: "", principioAtivo: "", concentracao: "", formaFarmaceutica: "",
  classeTerapeutica: null, listaPortaria344: "", usoContinuo: false,
  tipoUso: 1, exigeReceita: false, tipoReceituarioPadrao: null, unidadeMedidaSngpc: 1,
  generico: false, similar: false, referencia: false,
  cnpjFabricante: "", nomeFabricante: "",
};

// Listas da Portaria 344/98 (controle especial).
const LISTAS_344 = ["A1", "A2", "A3", "B1", "B2", "C1", "C2", "C3", "C4", "C5", "D1", "D2", "E", "F"];

interface Props {
  produtoId: string;
}

/**
 * Seção SNGPC/ANVISA do produto (relação 1:1). Recurso independente do
 * formulário principal — salvo via PUT /api/produtos/{id}/medicamento.
 */
export default function MedicamentoSngpc({ produtoId }: Props) {
  const { medicamento, isLoading, salvarMedicamento, salvando } = useProdutoMedicamento(produtoId);
  const [form, setForm] = useState<SalvarProdutoMedicamentoRequest>(EMPTY);

  useEffect(() => {
    if (medicamento) {
      setForm({
        registroMs: medicamento.registroMs ?? "",
        codigoDcb: medicamento.codigoDcb ?? "",
        principioAtivo: medicamento.principioAtivo ?? "",
        concentracao: medicamento.concentracao ?? "",
        formaFarmaceutica: medicamento.formaFarmaceutica ?? "",
        classeTerapeutica: medicamento.classeTerapeutica ?? null,
        listaPortaria344: medicamento.listaPortaria344 ?? "",
        usoContinuo: medicamento.usoContinuo,
        tipoUso: medicamento.tipoUso,
        exigeReceita: medicamento.exigeReceita,
        tipoReceituarioPadrao: medicamento.tipoReceituarioPadrao ?? null,
        unidadeMedidaSngpc: medicamento.unidadeMedidaSngpc,
        generico: medicamento.generico,
        similar: medicamento.similar,
        referencia: medicamento.referencia,
        cnpjFabricante: medicamento.cnpjFabricante ?? "",
        nomeFabricante: medicamento.nomeFabricante ?? "",
      });
    }
  }, [medicamento]);

  const set = <K extends keyof SalvarProdutoMedicamentoRequest>(
    k: K, v: SalvarProdutoMedicamentoRequest[K],
  ) => setForm((p) => ({ ...p, [k]: v }));

  const controleEspecial = form.classeTerapeutica === 2;

  const handleSalvar = async () => {
    await salvarMedicamento({
      ...form,
      // Lista 344 só vale para controle especial.
      listaPortaria344: controleEspecial ? form.listaPortaria344 : null,
    });
  };

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Carregando dados SNGPC...</div>;

  return (
    <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-6">
      <div>
        <h3 className="text-base font-semibold">SNGPC / ANVISA</h3>
        <p className="text-sm text-muted-foreground">
          Dados regulatórios para escrituração de medicamentos sujeitos a controle
          especial (Portaria 344/98) e antimicrobianos (IN 7/2011).
        </p>
      </div>

      {/* ── Identificação ANVISA ─────────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold">Identificação ANVISA</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Registro MS</Label>
            <Input maxLength={14} value={form.registroMs ?? ""} placeholder="13 ou 14 dígitos"
              onChange={(e) => set("registroMs", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Código DCB</Label>
            <Input maxLength={5} value={form.codigoDcb ?? ""} placeholder="5 dígitos (insumo)"
              onChange={(e) => set("codigoDcb", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Princípio ativo</Label>
            <Input maxLength={200} value={form.principioAtivo ?? ""}
              onChange={(e) => set("principioAtivo", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Concentração</Label>
            <Input maxLength={60} value={form.concentracao ?? ""} placeholder="ex: 500mg/mL"
              onChange={(e) => set("concentracao", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Forma farmacêutica</Label>
            <Input maxLength={60} value={form.formaFarmaceutica ?? ""} placeholder="comprimido, solução..."
              onChange={(e) => set("formaFarmaceutica", e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Classificação SNGPC ──────────────────────────────── */}
      <div className="space-y-3 border-t pt-4">
        <h4 className="text-sm font-semibold">Classificação SNGPC</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Classe terapêutica</Label>
            <Select
              value={String(form.classeTerapeutica ?? 0)}
              onValueChange={(v) => set("classeTerapeutica", Number(v) === 0 ? null : Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Não-controlado</SelectItem>
                <SelectItem value="1">Antimicrobiano</SelectItem>
                <SelectItem value="2">Sujeito a Controle Especial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {controleEspecial && (
            <div className="space-y-1">
              <Label>Lista Portaria 344/98</Label>
              <Select
                value={form.listaPortaria344 || ""}
                onValueChange={(v) => set("listaPortaria344", v)}
              >
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {LISTAS_344.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Switch checked={form.usoContinuo} onCheckedChange={(v) => set("usoContinuo", v)} />
          <Label>Uso contínuo / prolongado</Label>
        </div>
      </div>

      {/* ── Dispensação ──────────────────────────────────────── */}
      <div className="space-y-3 border-t pt-4">
        <h4 className="text-sm font-semibold">Dispensação</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Tipo de uso</Label>
            <Select value={String(form.tipoUso)} onValueChange={(v) => set("tipoUso", Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Humano</SelectItem>
                <SelectItem value="2">Veterinário</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Receituário padrão</Label>
            <Select
              value={String(form.tipoReceituarioPadrao ?? 0)}
              onValueChange={(v) => set("tipoReceituarioPadrao", Number(v) === 0 ? null : Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">— Nenhum —</SelectItem>
                <SelectItem value="1">Controle Especial 2 vias (Branca)</SelectItem>
                <SelectItem value="2">Notificação de Receita B (Azul)</SelectItem>
                <SelectItem value="3">Notificação de Receita Especial (Branca)</SelectItem>
                <SelectItem value="4">Notificação de Receita A (Amarela)</SelectItem>
                <SelectItem value="5">Antimicrobiano 2 vias</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Unidade de medida (SNGPC)</Label>
            <Select value={String(form.unidadeMedidaSngpc)} onValueChange={(v) => set("unidadeMedidaSngpc", Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Caixas</SelectItem>
                <SelectItem value="2">Frascos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Switch checked={form.exigeReceita} onCheckedChange={(v) => set("exigeReceita", v)} />
          <Label>Exige apresentação de receita</Label>
        </div>
      </div>

      {/* ── Classificação comercial ──────────────────────────── */}
      <div className="space-y-3 border-t pt-4">
        <h4 className="text-sm font-semibold">Classificação comercial</h4>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <Switch checked={form.generico} onCheckedChange={(v) => set("generico", v)} />
            <Label>Genérico</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.similar} onCheckedChange={(v) => set("similar", v)} />
            <Label>Similar</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.referencia} onCheckedChange={(v) => set("referencia", v)} />
            <Label>Referência</Label>
          </div>
        </div>
      </div>

      {/* ── Fabricante ───────────────────────────────────────── */}
      <div className="space-y-3 border-t pt-4">
        <h4 className="text-sm font-semibold">Fabricante</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>CNPJ do fabricante</Label>
            <Input maxLength={14} value={form.cnpjFabricante ?? ""} placeholder="14 dígitos, sem máscara"
              onChange={(e) => set("cnpjFabricante", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Nome do fabricante</Label>
            <Input maxLength={100} value={form.nomeFabricante ?? ""}
              onChange={(e) => set("nomeFabricante", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="flex justify-end border-t pt-4">
        <Button type="button" onClick={handleSalvar} disabled={salvando}>
          {salvando ? "Salvando..." : "Salvar dados SNGPC"}
        </Button>
      </div>
    </div>
  );
}
